import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { safeSetLocalStorage } from '@wwe-portal/web/libs/storage/safe-local-storage';
import { useGetCurrentUserId } from '@wwe-portal/web/providers/stores/auth-store';
import { STORAGE_ITEM_INTERSTITIAL_HISTORY } from '@wwe-portal/web/utils/constants/keys';
import { PAGE_NAMES } from '@wwe-portal/web/utils/constants/pages';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

interface InterstitialHistory {
  userId: string;
  updateKey: string;
  pageName: string;
}

function isInterstitialHistory(data: unknown): data is InterstitialHistory[] {
  return (
    Array.isArray(data) &&
    data.every(
      (item) => 'userId' in item && 'updateKey' in item && 'pageName' in item
    )
  );
}

export const getInterstitialHistory: () => InterstitialHistory[] = () => {
  try {
    const dataString = localStorage.getItem(STORAGE_ITEM_INTERSTITIAL_HISTORY);
    if (!dataString) return [];
    const data = JSON.parse(dataString);
    if (isInterstitialHistory(data)) {
      return data;
    }
    return [];
  } catch (e) {
    addDDError(e);
    return [];
  }
};

export const useGetInterstitialHistoryByPageName = () => {
  const pathname = usePathname();
  const userId = useGetCurrentUserId();

  const getInterstitialHistoryByPageName = () => {
    const interstitialHistory = getInterstitialHistory();
    const pageName = PAGE_NAMES[pathname];
    return interstitialHistory.filter(
      (item) => item.pageName === pageName && userId === item.userId
    );
  };
  return {
    getInterstitialHistoryByPageName,
  };
};

export const useTrackInterstitialHistory = (
  visible: boolean,
  updateKey: string
) => {
  const pathname = usePathname();
  const userId = useGetCurrentUserId();
  const pushInterstitialHistory = (updateKey: string) => {
    if (!userId) return;
    const interstitialHistory = getInterstitialHistory();
    const nextInterstitialHistory = [...interstitialHistory];
    const pageName = PAGE_NAMES[pathname];
    nextInterstitialHistory.push({
      pageName,
      updateKey,
      userId,
    });
    safeSetLocalStorage(STORAGE_ITEM_INTERSTITIAL_HISTORY, () =>
      JSON.stringify(nextInterstitialHistory)
    );
  };

  useEffect(() => {
    if (visible) {
      pushInterstitialHistory(updateKey);
    }
  }, [visible]);
};
